import { useEffect } from "react";
import { Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import { SessionTimeoutWatcher } from "@/components/admin/SessionTimeoutWatcher";

export function isAdminAuthenticated(): boolean {
  try {
    const token = localStorage.getItem("accessToken");
    if (!token) return false;

    // Un token dont l'expiration n'est pas renseignée, ou déjà dépassée, est traité
    // comme non authentifié — évite qu'une valeur bricolée dans le localStorage
    // (ex. via les devtools) laisse passer la garde de route.
    const expiresAtRaw = localStorage.getItem("tokenExpiresAt");
    const expiresAt = Number(expiresAtRaw);
    if (!expiresAtRaw || Number.isNaN(expiresAt) || expiresAt <= Date.now()) return false;

    return true;
  } catch {
    return false;
  }
}

export function getStoredRoles(): string[] {
  try {
    const raw = localStorage.getItem("roles");
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function hasAnyRole(allowed: string[]): boolean {
  const roles = getStoredRoles();
  return roles.some((r) => allowed.includes(r));
}

export function estPartenaireSeul(): boolean {
  return hasAnyRole(["PARTENAIRE"]) && !hasAnyRole(["ADMIN", "EDITEUR"]);
}

/**
 * Un partenaire (financier, lecture seule statistiques) n'a pas accès aux
 * pages de création/édition de contenu — le backend refuse déjà ces actions
 * (403), mais on évite en plus de lui laisser voir un formulaire qu'il ne
 * pourra jamais soumettre en le renvoyant directement vers les statistiques.
 */
export function useBloquerPartenaire() {
  const navigate = useNavigate();
  useEffect(() => {
    if (estPartenaireSeul()) {
      navigate("/redaction/analytics", { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

export function adminLogout() {
  try {
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("tokenExpiresAt");
    localStorage.removeItem("roles");
  } catch {}
}

function RequireAdmin() {
  const location = useLocation();
  if (!isAdminAuthenticated()) {
    return <Navigate to="/redaction/login" replace state={{ from: location.pathname }} />;
  }
  return (
    <>
      <SessionTimeoutWatcher />
      <Outlet />
    </>
  );
}

export default RequireAdmin;
